import {
  Season,
  SeasonPointsOverrides,
} from "model/Tournament/Segment/Season/Season";
import { selectMatches, selectMatchesRosterIds } from "selectors/Match";
import { EmptyGrowPanelText, ErrorText } from "styles/text";
import { CupModeSeasonTable } from "./CupMode/CupModeSeasonTable";
import { SetSeasonTable } from "./WorldTour/WorldTourSeasonTable";
import { MultiMmSeasonTable } from "./MultiMm/MultiMmSeasonTable";
import {
  AltSeasonPointsRules,
  SeasonPointsRules,
} from "model/Tournament/Segment/Season/SeasonPointRules";
import { BymcSeasonTable } from "./CupMode/BymcSeasonTable";
import { AdlSeasonTable } from "./CupMode/AdlSeasonTable";
import { BiplSeasonTable } from "./CupMode/BiplSeasonTable";
import { MajiMondaySeasonTable } from "./CupMode/MajiMondaySeasonTable";
import { NacSeasonTable } from "./CupMode/NacSeasonTable";
import { NacSeedingTable } from "./CupMode/NacSeedingTable";
import { AlamoSeasonTable } from "./CupMode/AlamoSeasonTable";
import { WmcSeasonTable } from "./CupMode/WmcSeasonTable";
import { SmsSeasonTable } from "./SmsSeasonTable";
import { BdlSeasonTable } from "./BdlSeasonTable";
import { useAppSelector } from "app/hooks";
import { AMSeasonTable } from "./AMSeasonTable";
import { OmniumSeasonTable } from "./OmniumSeasonTable";
import { CscSeasonTable } from "./CupMode/CscSeasonTable";
import { BonkCupSeasonTable } from "./CupMode/BonkCupSeasonTable";
import { SacSeasonTable } from "./CupMode/SacSeasonTable";

interface Props {
  season: Season;
  seasonPoints: SeasonPointsRules | undefined;
  seasonPointsOverrides: SeasonPointsOverrides | undefined;
  altSeasonPoints: AltSeasonPointsRules[] | undefined;
  tournamentId: string;
  primaryColor?: string;
  secondaryColor?: string;
}

export const SeasonTable = ({
  season,
  seasonPoints,
  seasonPointsOverrides,
  altSeasonPoints,
  tournamentId,
  primaryColor,
  secondaryColor,
}: Props) => {
  const rosterIds = useAppSelector((state) =>
    selectMatchesRosterIds(
      state,
      season.rounds.flatMap((r) => r.matchIds)
    )
  );

  const matchesByPlayday = useAppSelector((state) =>
    season.rounds.map((r) => selectMatches(state, r.matchIds))
  );

  const matches = matchesByPlayday.flat();

  if (tournamentId === "Bdlx1") {
    const completeMatchesByPlayday = matchesByPlayday.map((p) =>
      p.filter((m) => m.isComplete)
    );

    return (
      <BdlSeasonTable
        seasonPointsOverrides={seasonPointsOverrides}
        matchesByPlayday={completeMatchesByPlayday}
        numberOfPlaydays={season.rounds.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId === "OmniumTeamsx1") {
    const completeMatchesByPlayday = matchesByPlayday.map((p) =>
      p.filter((m) => m.isComplete)
    );

    return (
      <OmniumSeasonTable
        matchesByPlayday={completeMatchesByPlayday}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId === "AmericasMastersx1") {
    const completeMatchesByPlayday = matchesByPlayday.map((p) =>
      p.filter((m) => m.isComplete)
    );

    return (
      <AMSeasonTable
        seasonPointsOverrides={seasonPointsOverrides}
        matchesByPlayday={completeMatchesByPlayday}
        numberOfPlaydays={season.rounds.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId === "NcsaSacx7") {
    const completeMatches = matches.filter((m) => m.isComplete);
    return (
      <SacSeasonTable
        rosterIds={rosterIds}
        matches={completeMatches}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId === "NcsaNacx3" || tournamentId === "NcsaNacx4") {
    const completeMatchesByPlayday = matchesByPlayday.map((p) =>
      p.filter((m) => m.isComplete)
    );

    if (season.rounds.length === 2) {
      return (
        <NacSeedingTable
          seasonPointsOverrides={seasonPointsOverrides}
          matchesByPlayday={completeMatchesByPlayday}
          numberOfPlaydays={season.rounds.length}
          rosterIds={rosterIds}
          tournamentId={tournamentId}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
      );
    }

    return (
      <NacSeasonTable
        seasonPoints={seasonPoints}
        seasonPointsOverrides={seasonPointsOverrides}
        altSeasonPoints={altSeasonPoints}
        matchesByPlayday={completeMatchesByPlayday}
        numberOfPlaydays={season.rounds.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId === "Alamo") {
    const matches = matchesByPlayday.flat();
    const completeMatches = matches.filter((m) => m.isComplete);

    return (
      <AlamoSeasonTable
        seasonPoints={seasonPoints}
        seasonPointsOverrides={seasonPointsOverrides}
        altSeasonPoints={altSeasonPoints}
        matches={completeMatches}
        numberOfMatches={matches.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId.startsWith("Wmcx")) {
    const matches = matchesByPlayday.flat();
    const completeMatches = matches.filter((m) => m.isComplete);

    return (
      <WmcSeasonTable
        seasonPoints={seasonPoints}
        seasonPointsOverrides={seasonPointsOverrides}
        altSeasonPoints={altSeasonPoints}
        matches={completeMatches}
        numberOfMatches={matches.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId.startsWith("Cscx")) {
    const completeMatchesByPlayday = matchesByPlayday.map((p) =>
      p.filter((m) => m.isComplete)
    );

    return (
      <CscSeasonTable
        seasonPointsOverrides={seasonPointsOverrides}
        matchesByPlayday={completeMatchesByPlayday}
        numberOfPlaydays={season.rounds.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId === "MajiMondayx4") {
    const matches = matchesByPlayday.flat();
    const completeMatches = matches.filter((m) => m.isComplete);

    return (
      <MajiMondaySeasonTable
        seasonPoints={seasonPoints}
        seasonPointsOverrides={seasonPointsOverrides}
        altSeasonPoints={altSeasonPoints}
        matches={completeMatches}
        numberOfMatches={matches.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId === "BonkCup") {
    const matches = matchesByPlayday.flat();
    const completeMatches = matches.filter((m) => m.isComplete);

    return (
      <BonkCupSeasonTable
        seasonPoints={seasonPoints}
        seasonPointsOverrides={seasonPointsOverrides}
        altSeasonPoints={altSeasonPoints}
        matches={completeMatches}
        numberOfMatches={matches.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (!!altSeasonPoints) {
    const completeMatchesByPlayday = matchesByPlayday.map((p) =>
      p.filter((m) => m.isComplete)
    );

    return (
      <BymcSeasonTable
        seasonPoints={seasonPoints}
        seasonPointsOverrides={seasonPointsOverrides}
        altSeasonPoints={altSeasonPoints}
        matchesByPlayday={completeMatchesByPlayday}
        numberOfPlaydays={season.rounds.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId === "Adlx2") {
    const completeMatchesByPlayday = matchesByPlayday.map((p) =>
      p.filter((m) => m.isComplete)
    );

    return (
      <AdlSeasonTable
        seasonPoints={seasonPoints}
        seasonPointsOverrides={seasonPointsOverrides}
        matchesByPlayday={completeMatchesByPlayday}
        numberOfPlaydays={season.rounds.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId === "Biplx4") {
    const completeMatchesByPlayday = matchesByPlayday.map((p) =>
      p.filter((m) => m.isComplete)
    );

    return (
      <BiplSeasonTable
        seasonPoints={seasonPoints}
        matchesByPlayday={completeMatchesByPlayday}
        numberOfPlaydays={season.rounds.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        forfeitRosterIds={[]}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (tournamentId === "Smsx1") {
    const completeMatches = matches.filter((m) => m.isComplete);

    return (
      <SmsSeasonTable
        matches={completeMatches}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
      />
    );
  }

  if (
    matches.every((m) =>
      m.sets.every(
        (s) => s.type === "ForwardCupMode" || s.type === "ReverseCupMode"
      )
    )
  ) {
    const completeMatchesByPlayday = matchesByPlayday.map((p) =>
      p.filter((m) => m.isComplete)
    );

    return (
      <CupModeSeasonTable
        seasonPoints={seasonPoints}
        pointsOverrides={seasonPointsOverrides}
        matchesByPlayday={completeMatchesByPlayday}
        numberOfPlaydays={season.rounds.length}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
        forfeitRosterIds={[]}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    );
  }

  if (matches.every((m) => m.sets.every((s) => s.type === "Matchmaking"))) {
    const completeMatches = matches.filter((m) => m.isComplete);

    return (
      <MultiMmSeasonTable
        matches={completeMatches}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
      />
    );
  }

  if (matches.every((m) => m.sets.every((s) => s.type === "WorldTour"))) {
    const completeMatches = matches.filter((m) => m.isComplete);

    return (
      <SetSeasonTable
        matches={completeMatches}
        rosterIds={rosterIds}
        tournamentId={tournamentId}
      />
    );
  }

  return (
    <EmptyGrowPanelText>
      <ErrorText>Unable to calculate table for this season</ErrorText>
    </EmptyGrowPanelText>
  );
};
